import gsap from 'gsap';
import ScrollToPlugin from 'gsap/ScrollToPlugin';
import { observeScrollAnimationTriggerElement } from '~/lib/animations';
import { OKENDO_BASE_URL, OKENDO_STORE_URL } from '~/lib/constants';
import { generateStarRating } from '~/lib/helpers';
import { defineElement } from '~/utils';
import { calculateDateDifference } from '~/utils';

gsap.registerPlugin(ScrollToPlugin);

const attributes = {
  productId: 'data-product-id',
  reviewsListContainer: 'data-reviews-list',
  loadMoreButton: 'data-load-more',
  animationEnabled: 'data-animation-enabled',
  loader: 'data-reviews-widget-loader',
  limit: 'data-limit',
};

export class ReviewsList extends HTMLElement {
  constructor() {
    super();

    this.PRODUCT_REVIEWS_LIST_PARAMS = {
      limit: this.getAttribute(attributes.limit) || 5,
    };

    this.$reviewsListContainer = this.querySelector(`[${attributes.reviewsListContainer}]`);
    this.$loadMoreButton = this.querySelector(`[${attributes.loadMoreButton}]`);
    this.animationEnabled = this.getAttribute(attributes.animationEnabled) === 'true';

    this.reviewsWidgetModal = this.closest('.reviews-widget-modal');
    this.reviewsWidgetLoader = this.reviewsWidgetModal?.querySelector(`[${attributes.loader}]`);

    this.dataLoaded = false;
  }

  connectedCallback() {
    this.attachEventListeners();

    this.reviewsWidgetModal.addEventListener('modal-open', async () => {
      if (this.dataLoaded) return;
      const reviewsUrl =
        OKENDO_STORE_URL + `/reviews?${new URLSearchParams(this.PRODUCT_REVIEWS_LIST_PARAMS).toString()}`;
      const data = await this.fetchReviews(reviewsUrl);
      if (!data?.reviews) return;
      this.renderReviews(data.reviews, false, true);
      this.dataLoaded = true;
    });
  }

  attachEventListeners() {
    console.log('[ReviewsList] attachEventListeners called, button:', this.$loadMoreButton);
    this.$loadMoreButton.addEventListener('click', async () => {
      console.log('[ReviewsList] Load more clicked');
      this.$loadMoreButton.setAttribute('disabled', '');
      this.$loadMoreButton.textContent = 'Loading...';

      const nextUrl = this.$loadMoreButton.getAttribute('data-next-url');
      console.log('[ReviewsList] nextUrl:', nextUrl);
      if (!nextUrl) {
        console.log('[ReviewsList] No nextUrl, hiding button');
        this.$loadMoreButton.setAttribute('hidden', '');
        return;
      }

      const nextReviewsUrl = OKENDO_BASE_URL + nextUrl;
      console.log('[ReviewsList] Fetching:', nextReviewsUrl);
      const data = await this.fetchReviews(nextReviewsUrl);
      console.log('[ReviewsList] Fetch response:', data);

      if (!data?.reviews) {
        console.log('[ReviewsList] No reviews in response, resetting button');
        this.$loadMoreButton.removeAttribute('disabled');
        this.$loadMoreButton.textContent = 'Load More';
        return;
      }

      this.loadMoreReviews(data.reviews);
    });
  }

  async fetchReviews(url) {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        console.error(`Failed to fetch reviews. HTTP status: ${response.status}`);
        return null;
      }

      const data = await response.json();

      // Set the next URL on the load more button if it exists
      if (data?.nextUrl) {
        this.$loadMoreButton.setAttribute('data-next-url', data.nextUrl);
        this.$loadMoreButton.removeAttribute('hidden');
      } else {
        this.$loadMoreButton.removeAttribute('data-next-url');
        this.$loadMoreButton.setAttribute('hidden', '');
      }

      return data || {};
    } catch (error) {
      console.error(`Error fetching reviews:`, error);
      return null;
    }
  }

  async loadMoreReviews(reviews) {
    let $firstReview = null;

    // Create a visually hidden focusable element to move focus to after loading more reviews
    const visuallyHiddenFocusableElement = document.createElement('span');
    visuallyHiddenFocusableElement.setAttribute('tabindex', '-1');
    visuallyHiddenFocusableElement.classList.add('visually-hidden');
    visuallyHiddenFocusableElement.textContent = 'Loaded more reviews';
    this.$reviewsListContainer.appendChild(visuallyHiddenFocusableElement);

    reviews.forEach((review, index) => {
      const $reviewItem = this.buildReviewItem(review);
      if (!$reviewItem) return;

      this.$reviewsListContainer.appendChild($reviewItem);

      if (this.animationEnabled) {
        $reviewItem.classList.add('scroll-trigger', 'animate--slide-in');
        observeScrollAnimationTriggerElement($reviewItem);
      }

      if (index === 0) {
        $firstReview = $reviewItem;
      }
    });

    if ($firstReview) {
      this.scrollToFirstReview($firstReview);
    }

    this.$loadMoreButton.removeAttribute('disabled');
    this.$loadMoreButton.textContent = 'Load More';

    if (this.reviewsWidgetModal) {
      this.reviewsWidgetModal.dispatchEvent(
        new CustomEvent('modal-reinit-focus', {
          detail: {
            focusElement: visuallyHiddenFocusableElement,
          },
        })
      );

      // Remove the visually hidden focusable element after a delay
      setTimeout(() => {
        visuallyHiddenFocusableElement.innerHTML = '';
      }, 1500);
    }
  }

  scrollToFirstReview($firstReview) {
    const reviewsWidget = this.closest('.reviews-widget__inner');

    setTimeout(() => $firstReview.focus(), 100);

    gsap.to(reviewsWidget, {
      duration: 1,
      scrollTo: {
        y: $firstReview.offsetTop + 100,
        autoKill: false,
      },
      ease: 'power2.inOut',
    });
  }

  async renderReviews(reviews, clearExistingReviews = false, animate = false) {
    const $wrapper = this.closest('section') || this.closest('.reviews-widget');
    if (reviews.length === 0) {
      // If there are no reviews, hide the section
      if ($wrapper) $wrapper.style.display = 'none';
    } else {
      if ($wrapper) $wrapper.style.display = '';

      if (clearExistingReviews) {
        this.$reviewsListContainer.innerHTML = '';
      }

      reviews.forEach((review) => {
        const $reviewItem = this.buildReviewItem(review);
        if (!$reviewItem) return;
        this.$reviewsListContainer.appendChild($reviewItem);

        if (this.animationEnabled) {
          $reviewItem.classList.add('scroll-trigger', 'animate--slide-in');
          observeScrollAnimationTriggerElement($reviewItem);
        }
      });

      if (animate) {
        this.reviewsWidgetLoader.setAttribute('hidden', '');
        this.style.opacity = 1;
        this.style.transform = 'translateY(0)';

        if (this.reviewsWidgetModal) {
          this.reviewsWidgetModal.dispatchEvent(new CustomEvent('modal-reinit-focus'));
        }
      }
    }
  }

  buildReviewItem(review) {
    if (!review.productImageUrl || review.productHandle?.includes('shipping-protection')) return null;

    const $template = this.querySelector('[data-reviews-list-item-template]');
    const $clone = document.importNode($template.content, true);

    const $item = $clone.querySelector('.reviews-list__item');
    const $stars = $clone.querySelector('.reviews-list__item-stars');
    const $starsInner = $stars.firstElementChild;
    const $date = $clone.querySelector('.reviews-list__item-date');
    const $title = $clone.querySelector('.reviews-list__item-title');
    const $copy = $clone.querySelector('.reviews-list__item-copy');
    const $author = $clone.querySelector('.reviews-list__item-author');
    const $figure = $clone.querySelector('.reviews-list__item-figure');
    const $media = $clone.querySelector('.reviews-list__item-media');
    const $image = $media.querySelector('img');
    const $caption = $figure.querySelector('figcaption');

    $starsInner.setAttribute('aria-label', `Average rating: ${review.rating} out of 5`);
    $starsInner.innerHTML = generateStarRating(review.rating);
    $date.textContent = calculateDateDifference(review.dateCreated);

    if (review.title) $title.textContent = review.title;
    if (review.body) $copy.textContent = review.body;
    if (review.author) $author.textContent = review.author;
    if (review.productName) $caption.textContent = review.productName;

    if (review.productImageUrl) {
      $image.src = review.productImageUrl;
      $image.alt = '';
    } else {
      $image.style.display = 'none';
      $media.classList.add('reviews-list__item-media--no-image');
    }

    return $item;
  }
}

defineElement('reviews-list', ReviewsList);
